/**
 * Google Consent Mode v2 mapping (ui#256) — React-free, so the privacy contract is testable without
 * rendering anything.
 *
 * We run BASIC Consent Mode, not Advanced: no Google tag is loaded and no request of any kind reaches
 * Google until the visitor grants a purpose that needs one. That is why {@link googleTaggingAllowed}
 * exists at all — in Advanced mode the tag would load immediately and send a cookieless ping under
 * denied defaults, which is exactly the "no traffic before consent" property this launch promises.
 * The defaults below are still pushed first when the tag finally loads, because the tag must never
 * observe an unset signal.
 */

import type {ConsentState} from "consent/consentStore";

/** The two values Google's consent API accepts. */
export type ConsentSignal = "granted" | "denied";

/** The four Consent Mode v2 signals this app has an opinion about. */
export interface ConsentModeSignals {
	ad_storage: ConsentSignal;
	ad_user_data: ConsentSignal;
	ad_personalization: ConsentSignal;
	analytics_storage: ConsentSignal;
}

/** Pre-consent defaults: everything denied, pushed before any other tag command. */
export const CONSENT_DEFAULTS: ConsentModeSignals = {
	ad_storage: "denied",
	ad_user_data: "denied",
	ad_personalization: "denied",
	analytics_storage: "denied",
};

/**
 * Tag-level settings that are NOT consent signals and never vary by choice.
 *
 * `ads_data_redaction` keeps ad identifiers out of network requests while ad storage is denied.
 * `url_passthrough` stays false on purpose: passing a click identifier through page URLs would put it
 * in browser history, referrers and shared links — a place we cannot delete it from at retention time.
 */
export const REDACTION_SETTINGS = {ads_data_redaction: true, url_passthrough: false} as const;

function signal(granted: boolean): ConsentSignal {
	return granted ? "granted" : "denied";
}

/**
 * The visitor's choices expressed as Consent Mode signals.
 *
 * `ad_personalization` followed the advertising grant from ui#2675 onward. Until then it was hardcoded
 * denied, because the launch bought measurement only and deriving it would have let an edit to the
 * marketing toggle silently enable personalized advertising. That risk did not go away — it moved:
 * the signal may only follow a grant that was given while the banner, the settings panel and the
 * cookie policy actually DESCRIBED remarketing audiences.
 *
 * This function cannot check that itself. It is pure, and the description version lives on the stored
 * record, so the guarantee is `consent/consentStore`'s: it withdraws any advertising grant made
 * against an older description and reopens the banner, so `marketing` can only be true here for a
 * visitor who agreed to the current one. The pair is asserted together in `consentStore.test.ts`
 * ("keeps a pre-ui#2675 Accept all out of ad personalization"), because either half alone would pass
 * while the two were broken.
 */
export function consentModeSignals(consent: ConsentState): ConsentModeSignals {
	return {
		ad_storage: signal(consent.marketing),
		ad_user_data: signal(consent.marketing),
		ad_personalization: signal(consent.marketing),
		analytics_storage: signal(consent.analytics),
	};
}

/**
 * Whether a granted purpose justifies loading a Google tag at all.
 *
 * The ADVERTISING purpose alone, since kitecorp/transferify-api-service#1929 took GA4 out of the
 * browser. Only the Google Ads tag is left, and everything it does — the remarketing hit, the
 * identifiers it writes, even the cookieless ping it sends under `ad_storage: denied` — serves
 * advertising. Loading it for somebody who granted analytics and refused advertising would contact
 * Google for the one purpose they declined.
 *
 * It used to read `analytics || marketing`, and that was right while the same script also carried the
 * GA4 measurement id: one script, two purposes, and either grant justified fetching it. With the GA4
 * half gone that reasoning went with it. `.env.production` and the cookie policy both already say the
 * tag loads only on the advertising grant; this is the code agreeing with them.
 */
export function googleTaggingAllowed(consent: ConsentState): boolean {
	return consent.marketing;
}
